import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "./../../../../context/Appcontext";
import "./../../../../assets/css/mentor/mentordashboard/Auth/LoginSignup.css";
import mentorSignupImg from "./../../../../assets/images/mentorSignupImage.png";
import mentorLoginImg from "./../../../../assets/images/mentorLogin.png";

// ─── SVG Icons ───────────────────────────────────────────────
const IconEnvelope = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
  </svg>
);

const IconLock = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const IconEyeOpen = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeClosed = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// ─────────────────────────────────────────────────────────────
const MentorLoginSignup = () => {
  const navigate = useNavigate();
  const { baseUrl, regExpChecker } = useContext(AppContext);

  const [isLogin, setIsLogin] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [showAutoLoginSpinner, setShowAutoLoginSpinner] = useState(false);

  // ── Signup states ──
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // ── Login states ──
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPass, setShowLoginPass] = useState(false);

  // ── Shared ──
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setPageLoading(false), 500);
    return () => clearTimeout(t);
  }, []);

  const handleValidation = (field, value) => {
    const error = regExpChecker(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  // ── Auto-login after signup ──
  const autoLogin = (userEmail, userPassword) => {
    setShowAutoLoginSpinner(true);
    axios
      .post(`${baseUrl}/mentor/login`, {
        email: userEmail,
        password: userPassword,
      })
      .then((response) => {
        localStorage.setItem("mentorData", JSON.stringify(response.data.data));
        localStorage.setItem("isLoggedIn", "true");
        setTimeout(() => {
          setShowAutoLoginSpinner(false);
          navigate("/mentordashboard");
        }, 2000);
      })
      .catch(() => {
        setShowAutoLoginSpinner(false);
        setErrorMessage("Auto-login failed. Please login manually.");
        setIsLogin(true);
      });
  };

  // ── Signup ──
  const signupMentor = (e) => {
    e.preventDefault();
    setErrorMessage("");
    const hasErrors = Object.values(errors).some((err) => err !== "");
    if (hasErrors) {
      setErrorMessage("Please fix the errors before submitting");
      return;
    }
    if (!agreeTerms) {
      setErrorMessage("Please accept our Terms and Privacy Policy to continue");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    setIsLoading(true);
    axios
      .post(`${baseUrl}/mentor/signup`, {
        firstname,
        lastname,
        email,
        password,
        confirmpassword: confirmPassword,
        role: "Mentor",
      })
      .then(() => {
        setIsLoading(false);
        autoLogin(email, password);
      })
      .catch((error) => {
        setIsLoading(false);
        const data = error.response?.data;
        if (
          data?.errors?.email?.includes("This email is already registered.")
        ) {
          Swal.fire({
            title: "Account Already Exists!",
            text: "This email is already registered. Would you like to login instead?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#7d2937",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Login",
            cancelButtonText: "Cancel",
          }).then((result) => {
            if (result.isConfirmed) setIsLogin(true);
          });
        } else if (data?.errors) {
          setErrorMessage(Object.values(data.errors).flat().join(", "));
        } else {
          setErrorMessage(data?.message || "Signup failed. Please try again.");
        }
      });
  };

  // ── Login ──
  const loginMentor = (e) => {
    e.preventDefault();
    setErrorMessage("");
    const hasErrors = Object.values(errors).some((err) => err !== "");
    if (hasErrors) {
      setErrorMessage("Please fix the errors before submitting");
      return;
    }

    setIsLoading(true);
    axios
      .post(`${baseUrl}/mentor/login`, {
        email: loginEmail,
        password: loginPassword,
      })
      .then((response) => {
        setIsLoading(false);
        localStorage.setItem("mentorData", JSON.stringify(response.data.data));
        localStorage.setItem("isLoggedIn", "true");
        const userName = response.data.data?.firstname || "Mentor";
        Swal.fire({
          icon: "success",
          title: `Welcome back, ${userName}!`,
          text: "Login successful",
          showConfirmButton: false,
          timer: 1500,
        }).then(() => navigate("/mentordashboard"));
      })
      .catch((error) => {
        setIsLoading(false);
        const data = error.response?.data;
        setErrorMessage(
          data?.message || "Invalid credentials. Please try again.",
        );
      });
  };

  // ─── Auto-login spinner overlay ───
  if (showAutoLoginSpinner) {
    return (
      <div className="ml-spinner-overlay">
        <div className="ml-spinner-content">
          <div className="ml-spinner-ring" />
          <h3 className="ml-spinner-text">
            Welcome! Setting up your dashboard…
          </h3>
          <p className="ml-spinner-sub">Please wait a moment</p>
        </div>
      </div>
    );
  }

  // ─── Page loader ───
  if (pageLoading) {
    return (
      <div className="ml-page-loader">
        <div className="ml-page-spinner" />
      </div>
    );
  }

  // ══════════════════════════════════════════════
  //  SIGNUP SCREEN
  //  Left 38%: garnet bg + garnet card | Right 62%: image
  // ══════════════════════════════════════════════
  if (!isLogin) {
    return (
      <div className="mentor-page">
        {/* ── LEFT 38% — garnet bg + garnet card ── */}
        <div className="mentor-su-right">
          <div className="mentor-su-card">
            <div className="mentor-su-header">
              <h1 className="mentor-su-title">Create Account</h1>
              <p className="mentor-su-subtitle">
                Start your mentoring journey with{" "}
                <strong>TechMentor Hub</strong>
              </p>
            </div>

            <form onSubmit={signupMentor} noValidate>
              {/* Name row */}
              <div className="mentor-su-name-row">
                <div className="mentor-su-field">
                  <label className="mentor-su-label">First Name</label>
                  <input
                    className="mentor-su-input"
                    type="text"
                    placeholder="First Name"
                    value={firstname}
                    onChange={(e) => {
                      setFirstname(e.target.value);
                      handleValidation("firstname", e.target.value);
                    }}
                    autoComplete="given-name"
                  />
                  {errors.firstname && (
                    <span className="mentor-field-error">
                      {errors.firstname}
                    </span>
                  )}
                </div>
                <div className="mentor-su-field">
                  <label className="mentor-su-label">Last Name</label>
                  <input
                    className="mentor-su-input"
                    type="text"
                    placeholder="Last Name"
                    value={lastname}
                    onChange={(e) => {
                      setLastname(e.target.value);
                      handleValidation("lastname", e.target.value);
                    }}
                    autoComplete="family-name"
                  />
                  {errors.lastname && (
                    <span className="mentor-field-error">
                      {errors.lastname}
                    </span>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="mentor-su-field">
                <label className="mentor-su-label">Email Address</label>
                <input
                  className="mentor-su-input"
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    handleValidation("email", e.target.value);
                  }}
                  autoComplete="email"
                />
                {errors.email && (
                  <span className="mentor-field-error">{errors.email}</span>
                )}
              </div>

              {/* Password */}
              <div className="mentor-su-field">
                <label className="mentor-su-label">Password</label>
                <div className="mentor-su-input-wrap">
                  <input
                    className="mentor-su-input"
                    type={showPass ? "text" : "password"}
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      handleValidation("password", e.target.value);
                    }}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="mentor-su-eye"
                    onClick={() => setShowPass((v) => !v)}
                    aria-label="Toggle password"
                  >
                    {showPass ? <IconEyeClosed /> : <IconEyeOpen />}
                  </button>
                </div>
                {errors.password && (
                  <span className="mentor-field-error">{errors.password}</span>
                )}
              </div>

              {/* Confirm Password */}
              <div className="mentor-su-field">
                <label className="mentor-su-label">Confirm Password</label>
                <div className="mentor-su-input-wrap">
                  <input
                    className="mentor-su-input"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword:
                          e.target.value !== password
                            ? "Passwords do not match"
                            : "",
                      }));
                    }}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="mentor-su-eye"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label="Toggle confirm password"
                  >
                    {showConfirm ? <IconEyeClosed /> : <IconEyeOpen />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="mentor-field-error">
                    {errors.confirmPassword}
                  </span>
                )}
              </div>

              {/* Terms */}
              <label className="mentor-su-terms-row">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
                <span className="mentor-su-check-box" aria-hidden="true" />
                <span className="mentor-su-terms-label">
                  I agree to{" "}
                  <a href="/terms" onClick={(e) => e.stopPropagation()}>
                    Terms &amp; Privacy Policy
                  </a>
                </span>
              </label>

              {errorMessage && (
                <div className="mentor-su-error" role="alert">
                  {errorMessage}
                </div>
              )}

              <button
                type="submit"
                className="mentor-su-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="mentor-su-btn-spinner"
                      aria-hidden="true"
                    />{" "}
                    Creating…
                  </>
                ) : (
                  "SIGN UP"
                )}
              </button>
            </form>

            <p className="mentor-su-login-row">
              Already have an account?
              <button
                type="button"
                className="mentor-su-login-link"
                onClick={() => {
                  setIsLogin(true);
                  setErrors({});
                  setErrorMessage("");
                }}
              >
                Login
              </button>
            </p>
          </div>
        </div>

        {/* ── RIGHT 62% — image + text overlay ── */}
        <div className="mentor-su-left">
          <div className="mentor-su-illus-wrap">
            <img
              src={mentorSignupImg}
              alt="Mentor illustration"
              className="mentor-su-illus"
            />
          </div>

          <div className="mentor-su-left-inner">
            <p className="mentor-su-lt-top">Welcome to</p>
            <h2 className="mentor-su-lt-title">TechMentor Hub</h2>
            <p className="mentor-su-lt-sub">
              Create your account and start your mentoring journey.
            </p>
          </div>

          <button
            className="mentor-su-back-btn"
            onClick={() => navigate("/")}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════
  //  LOGIN SCREEN
  //  Left 38%: white bg + bone card | Right 62%: image
  // ══════════════════════════════════════════════
  return (
    <div className="mentor-page">
      {/* ── LEFT 38% — white bg + bone card ── */}
      <div className="mentor-li-left-panel">
        <div className="mentor-li-card">
          <div className="mentor-li-card-header">
            <h1 className="mentor-li-title">Welcome back</h1>
            <h2 className="mentor-li-subtitle">Login</h2>
            <p className="mentor-li-desc">Enter your account details</p>
          </div>

          <form onSubmit={loginMentor} noValidate>
            {/* Email */}
            <div className="mentor-li-field">
              <label className="mentor-li-label" htmlFor="mentorLoginEmail">
                Email
              </label>
              <div className="mentor-li-input-wrap">
                <span className="mentor-li-input-icon" aria-hidden="true">
                  <IconEnvelope />
                </span>
                <input
                  className="mentor-li-input"
                  id="mentorLoginEmail"
                  type="email"
                  placeholder="Email"
                  value={loginEmail}
                  onChange={(e) => {
                    setLoginEmail(e.target.value);
                    handleValidation("email", e.target.value);
                  }}
                  autoComplete="email"
                  required
                />
              </div>
              {errors.email && (
                <span className="mentor-li-field-error">{errors.email}</span>
              )}
            </div>

            {/* Password */}
            <div className="mentor-li-field">
              <label className="mentor-li-label" htmlFor="mentorLoginPassword">
                Password
              </label>
              <div className="mentor-li-input-wrap">
                <span className="mentor-li-input-icon" aria-hidden="true">
                  <IconLock />
                </span>
                <input
                  className="mentor-li-input"
                  id="mentorLoginPassword"
                  type={showLoginPass ? "text" : "password"}
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => {
                    setLoginPassword(e.target.value);
                    handleValidation("password", e.target.value);
                  }}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="mentor-li-eye"
                  onClick={() => setShowLoginPass((v) => !v)}
                  aria-label="Toggle password"
                >
                  {showLoginPass ? <IconEyeClosed /> : <IconEyeOpen />}
                </button>
              </div>
              {errors.password && (
                <span className="mentor-li-field-error">{errors.password}</span>
              )}
            </div>

            {/* Meta */}
            <div className="mentor-li-meta">
              <label className="mentor-li-remember">
                <input type="checkbox" />
                <span className="mentor-li-check-box" aria-hidden="true" />
                Remember me
              </label>
              <button
                type="button"
                className="mentor-li-forgot"
                onClick={() => navigate("/mentor/forgot-password")}
              >
                Forgot password?
              </button>
            </div>

            {errorMessage && (
              <div className="mentor-li-error" role="alert">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              className="mentor-li-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="mentor-li-btn-spinner" aria-hidden="true" />{" "}
                  Logging in…
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>

          <p className="mentor-li-signup-row">
            Don't have an account?
            <button
              type="button"
              className="mentor-li-signup-link"
              onClick={() => {
                setIsLogin(false);
                setErrors({});
                setErrorMessage("");
              }}
            >
              Sign up
            </button>
          </p>
        </div>
      </div>

      {/* ── RIGHT 62% — image + text overlay ── */}
      <div className="mentor-li-right-panel">
        <div className="mentor-li-illus-wrap">
          <img
            src={mentorLoginImg}
            alt="Mentor portal illustration"
            className="mentor-li-illus"
          />
        </div>

        <div className="mentor-li-right-inner">
          <p className="mentor-li-rt-top">Welcome to</p>
          <h2 className="mentor-li-rt-title">mentor portal</h2>
          <p className="mentor-li-rt-sub">Login to access your dashboard</p>
        </div>

        <button className="mentor-li-back-btn" onClick={() => navigate("/")}>
          Back
        </button>
      </div>
    </div>
  );
};

export default MentorLoginSignup;
